import React, { useEffect, useMemo, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { InputField } from '../ds/InputField';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import {
  apartmentsApi,
  ApartmentRenumberBlock,
  ApartmentRenumberError,
  ApartmentRenumberItem,
  ApartmentRenumberPreview,
} from '../services/api';
import { APARTMENT_NUMBER_MAX, planRenumberRange, renumberDraftErrors } from '../utils/apartmentRenumber';
import { lokaleLabel } from '../utils/plural';

export interface ApartmentRenumberModalProps {
  /** Lokale budynku; `null` = okno zamknięte. */
  apartments: { id: string; apartmentNumber: string; displayLabel?: string | null }[] | null;
  buildingId: string;
  /** Adres budynku do tytułu. */
  subject?: string;
  onClose: () => void;
  /** Zapis się udał — rodzic odświeża budynek. */
  onSaved: () => void;
}

const banner = (kind: 'error' | 'ok' | 'info'): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-label)',
  background: kind === 'error' ? 'var(--status-deny-bg)' : kind === 'ok' ? 'var(--status-ok-bg)' : 'var(--surface-fill)',
  border: `var(--border-width) solid ${kind === 'error' ? 'var(--status-deny-bar)' : kind === 'ok' ? 'var(--status-ok-bar)' : 'var(--border)'}`,
  color: kind === 'error' ? 'var(--status-deny)' : kind === 'ok' ? 'var(--status-ok)' : 'var(--text-secondary)',
});

const td: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: 'var(--border-width) solid var(--border)',
  color: 'var(--text-primary)',
  verticalAlign: 'middle',
};

const cellInput = (invalid: boolean): React.CSSProperties => ({
  width: '100%',
  minWidth: 72,
  minHeight: 40,
  padding: 'var(--space-1) var(--space-2)',
  fontSize: 'var(--text-body)',
  fontFamily: 'var(--font-sans)',
  color: 'var(--text-primary)',
  background: 'var(--surface-card)',
  border: `var(--border-width) solid ${invalid ? 'var(--status-deny-bar)' : 'var(--border-strong)'}`,
  borderRadius: 'var(--radius-md)',
  boxSizing: 'border-box',
});

const byNumber = (a: { apartmentNumber: string }, b: { apartmentNumber: string }) =>
  String(a.apartmentNumber).localeCompare(String(b.apartmentNumber), 'pl', { numeric: true });

const toInt = (v: string): number => (v.trim() === '' ? NaN : Number(v.trim()));

/**
 * „Zmień numery lokali" (prośba właściciela 2026-10-21, Wolności 532c: lokale 1–10 z importu CEEB → 31–40). Zmienia
 * NUMER lokalu (tożsamość w CEEB), nie tylko oznaczenie na drzwiach. Dwa tryby: zakres („lokale 1–10 → od 31") i
 * edycja w wierszu. Najpierw „Sprawdź zmianę" — podgląd z serwera (stary → nowy numer, oznaczenia do wyczyszczenia,
 * bezpieczniki), dopiero potem zapis w jednej transakcji. POST /api/apartments/renumber (dryRun / zapis).
 */
export default function ApartmentRenumberModal({ apartments, buildingId, subject, onClose, onSaved }: ApartmentRenumberModalProps) {
  const open = !!apartments && apartments.length > 0;
  const sorted = useMemo(() => (open ? [...apartments!].sort(byNumber) : []), [open, apartments]);
  const key = open ? apartments!.map((a) => `${a.id}:${a.apartmentNumber}:${a.displayLabel ?? ''}`).join(',') : '';

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [range, setRange] = useState({ from: '', to: '', start: '' });
  const [rangeInfo, setRangeInfo] = useState<{ kind: 'error' | 'info'; text: string } | null>(null);
  const [preview, setPreview] = useState<ApartmentRenumberPreview | null>(null);
  /** Wyczyść zbędne oznaczenie na drzwiach — id lokalu → zaznaczone (domyślnie tak dla zbędnych). */
  const [clearLabel, setClearLabel] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<'preview' | 'save' | null>(null);
  const [error, setError] = useState<{ text: string; blocked?: ApartmentRenumberBlock[] } | null>(null);

  useEffect(() => {
    if (!open) return;
    setDrafts({});
    setRange({ from: '', to: '', start: '' });
    setRangeInfo(null);
    setPreview(null);
    setClearLabel({});
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const rowErrors = useMemo(() => renumberDraftErrors(sorted, drafts), [sorted, drafts]);
  const items: ApartmentRenumberItem[] = sorted
    .filter((a) => drafts[a.id] !== undefined && drafts[a.id].trim() !== '' && drafts[a.id].trim() !== a.apartmentNumber)
    .map((a) => ({ apartmentId: a.id, from: a.apartmentNumber, to: drafts[a.id].trim() }));
  const hasErrors = Object.keys(rowErrors).length > 0;

  const resetPreview = () => { setPreview(null); setError(null); };
  const setDraft = (id: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [id]: value }));
    resetPreview();
  };

  const applyRange = () => {
    const plan = planRenumberRange(sorted, { from: toInt(range.from), to: toInt(range.to), start: toInt(range.start) });
    if ('error' in plan) { setRangeInfo({ kind: 'error', text: plan.error }); return; }
    if (plan.rows.length === 0) {
      setRangeInfo({ kind: 'error', text: `W budynku nie ma lokali o numerach ${range.from.trim()}–${range.to.trim()}.` });
      return;
    }
    setDrafts((prev) => {
      const next = { ...prev };
      for (const r of plan.rows) next[r.apartmentId] = r.to;
      return next;
    });
    resetPreview();
    setRangeInfo({
      kind: 'info',
      text: `Wpisano nowe numery dla ${lokaleLabel(plan.rows.length)} — sprawdź w tabeli i kliknij „Sprawdź zmianę".`
        + (plan.skipped.length ? ` Pominięte (nietypowy numer — wpisz w wierszu): ${plan.skipped.join(', ')}.` : ''),
    });
  };

  const showError = (e: unknown, fallback: string) => {
    const err = e as ApartmentRenumberError;
    setError({ text: err && err.message ? err.message : fallback, blocked: err && err.blocked });
  };

  const check = async () => {
    if (items.length === 0 || hasErrors) return;
    setBusy('preview');
    setError(null);
    try {
      const p = await apartmentsApi.renumber(buildingId, items, true);
      setPreview(p);
      setClearLabel(Object.fromEntries(p.changes.filter((c) => c.labelRedundant).map((c) => [c.apartmentId, true])));
    } catch (e) {
      console.error('Podgląd zmiany numerów lokali nieudany:', e);
      setPreview(null);
      showError(e, 'Nie udało się sprawdzić zmiany. Sprawdź połączenie z serwerem.');
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (!preview || !preview.allowed) return;
    setBusy('save');
    setError(null);
    try {
      await apartmentsApi.renumber(buildingId, preview.changes.map((c) => ({
        apartmentId: c.apartmentId, from: c.from, to: c.to, clearDisplayLabel: c.labelRedundant && clearLabel[c.apartmentId] === true,
      })), false);
      onSaved();
      onClose();
    } catch (e) {
      console.error('Zmiana numerów lokali nieudana:', e);
      setPreview(null);
      showError(e, 'Nie udało się zmienić numerów. Sprawdź połączenie z serwerem.');
    } finally {
      setBusy(null);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={busy !== null}>Anuluj</Button>
      {!preview ? (
        <Button variant="primary" icon="check" onClick={check} disabled={busy !== null || items.length === 0 || hasErrors}>
          {busy === 'preview' ? 'Sprawdzanie…' : `Sprawdź zmianę (${items.length})`}
        </Button>
      ) : (
        <Button variant="primary" icon="check" onClick={save} disabled={busy !== null || !preview.allowed}>
          {busy === 'save' ? 'Zapisywanie…' : `Zmień numery (${preview.changes.length})`}
        </Button>
      )}
    </>
  );

  return (
    <ModalShell
      open={open}
      title={subject ? `Zmień numery lokali — ${subject}` : 'Zmień numery lokali'}
      width={760}
      onClose={onClose}
      footer={footer}
    >
      <div data-testid="apartment-renumber" style={{ display: 'grid', gap: 'var(--space-4)', fontFamily: 'var(--font-sans)' }}>
        <div role="note" style={banner('info')}>
          <Icon name="triangle-alert" size={18} />
          <span>
            Zmieniasz <strong>numer lokalu</strong> — ten sam numer bot wpisuje do CEEB, a nocny import dopasowuje po nim lokale
            (samo oznaczenie na drzwiach zmienisz w „Oznaczenia lokali”). Zapisy kontroli i źródła ciepła zostają przy lokalu.
            Stare numery dostają znacznik, więc nocny import nie założy ich ponownie z protokołów z poprzednich lat.
            W CEEB poprzednia kontrola zostaje pod starymi numerami: przy pierwszej wysyłce CEEB skopiuje te lokale do
            protokołu jako wersje robocze, a bot doda lokale pod nowymi numerami — stare kopie trzeba usunąć w CEEB
            przed wystawieniem protokołu (bot nie klika „Usuń”). Dlatego do wystawienia protokołu „Wyślij do CEEB” zapisuje
            tylko wersję roboczą — protokół wystawisz przyciskiem „Finalizuj w CEEB” po sprawdzeniu lokali w CEEB.
          </span>
        </div>

        <section aria-label="Zakres numerów" style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <strong style={{ fontSize: 'var(--text-body)', color: 'var(--text-heading)' }}>Zakres</strong>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <InputField label="Lokale od" inputMode="numeric" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} containerStyle={{ width: 110 }} fullWidth={false} />
            <InputField label="do" inputMode="numeric" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} containerStyle={{ width: 110 }} fullWidth={false} />
            <InputField label="Nowe numery od" inputMode="numeric" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} containerStyle={{ width: 140 }} fullWidth={false} />
            <Button variant="secondary" icon="check" onClick={applyRange}>Wypełnij</Button>
          </div>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
            Np. lokale 1–10 → nowe numery od 31: lokal 1 = 31, lokal 10 = 40. Pojedynczy numer wpisz w wierszu tabeli.
          </span>
          {rangeInfo && (
            <div role={rangeInfo.kind === 'error' ? 'alert' : 'status'} style={banner(rangeInfo.kind)}>
              <Icon name={rangeInfo.kind === 'error' ? 'triangle-alert' : 'check'} size={18} />
              <span>{rangeInfo.text}</span>
            </div>
          )}
        </section>

        {error && (
          <div role="alert" style={banner('error')}>
            <Icon name="triangle-alert" size={18} />
            <span>
              {error.text}
              {error.blocked && error.blocked.length > 1 && (
                <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)' }}>
                  {error.blocked.slice(1).map((b, i) => <li key={i}>{b.error}</li>)}
                </ul>
              )}
            </span>
          </div>
        )}

        {preview ? (
          <section aria-label="Podgląd zmiany" style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <strong style={{ fontSize: 'var(--text-body)', color: 'var(--text-heading)' }}>Podgląd</strong>
            {preview.blocked.length > 0 ? (
              <div role="alert" style={banner('error')}>
                <Icon name="triangle-alert" size={18} />
                <span>
                  Nie można zmienić numerów:
                  <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)' }}>
                    {preview.blocked.map((b, i) => <li key={i}>{b.error}</li>)}
                  </ul>
                </span>
              </div>
            ) : (
              <div role="status" style={banner('ok')}>
                <Icon name="check" size={18} />
                <span>
                  {`Zmiana ${lokaleLabel(preview.changes.length)} jest możliwa.`}
                  {preview.vacated.length > 0 && ` Zwolnione numery (nocny import ich nie założy): ${preview.vacated.join(', ')}.`}
                  {preview.reclaimed.length > 0 && ` Numery ${preview.reclaimed.join(', ')} należały do lokali usuniętych w aplikacji — znacznik zostanie zdjęty, nocny import znów będzie aktualizował te lokale z CEEB.`}
                </span>
              </div>
            )}
            <div style={{ overflow: 'auto', maxHeight: '40vh' }}>
              <table aria-label="Podgląd zmiany numerów" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
                <thead>
                  <tr style={{ background: 'var(--surface-fill)', textAlign: 'left' }}>
                    <th style={td}>Obecny numer</th>
                    <th style={td}>Nowy numer</th>
                    <th style={td}>Oznaczenie na drzwiach</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.changes.map((c) => (
                    <tr key={c.apartmentId} data-testid={`renumber-preview-${c.apartmentId}`}>
                      <td style={td}>{c.from}</td>
                      <td style={{ ...td, fontWeight: 'var(--weight-semibold)' as any }}>{c.to}</td>
                      <td style={td}>
                        {!c.displayLabel ? '—' : c.labelRedundant ? (
                          <label style={{ display: 'inline-flex', gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={clearLabel[c.apartmentId] === true}
                              onChange={(e) => setClearLabel((prev) => ({ ...prev, [c.apartmentId]: e.target.checked }))}
                              aria-label={`Wyczyść oznaczenie ${c.displayLabel} lokalu ${c.to}`}
                            />
                            {`„${c.displayLabel}” — takie samo jak nowy numer, wyczyść`}
                          </label>
                        ) : `„${c.displayLabel}” — zostaje`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <Button variant="secondary" icon="pencil" onClick={resetPreview} disabled={busy !== null}>Wróć do edycji</Button>
            </div>
          </section>
        ) : (
          <div style={{ overflow: 'auto', maxHeight: '40vh' }}>
            <table aria-label="Numery lokali" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
              <thead>
                <tr style={{ background: 'var(--surface-fill)', textAlign: 'left' }}>
                  <th style={td}>Obecny numer</th>
                  <th style={td}>Nowy numer</th>
                  <th style={td}>Oznaczenie na drzwiach</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((a) => {
                  const err = rowErrors[a.id];
                  const value = drafts[a.id] ?? '';
                  const changed = value.trim() !== '' && value.trim() !== a.apartmentNumber;
                  return (
                    <tr key={a.id} data-testid={`renumber-row-${a.id}`} style={changed ? { background: 'var(--surface-hover)' } : undefined}>
                      <td style={{ ...td, fontWeight: 'var(--weight-semibold)' as any, whiteSpace: 'nowrap' }}>{a.apartmentNumber}</td>
                      <td style={td}>
                        <input
                          aria-label={`Nowy numer lokalu ${a.apartmentNumber}`}
                          value={value}
                          placeholder={a.apartmentNumber}
                          maxLength={APARTMENT_NUMBER_MAX}
                          onChange={(e) => setDraft(a.id, e.target.value)}
                          style={cellInput(!!err)}
                        />
                        {err && <div role="alert" style={{ color: 'var(--status-deny)', fontSize: 'var(--text-caption)', marginTop: 'var(--space-1)' }}>{err}</div>}
                      </td>
                      <td style={{ ...td, color: 'var(--text-secondary)' }}>{a.displayLabel || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ModalShell>
  );
}
